import { scopedThreadKey } from "@cleo/client-runtime/environment";
import type { ScopedThreadRef } from "@cleo/contracts";
import { PencilLine, X } from "lucide-react";
import type { ReactElement } from "react";

import { composerDraftHasUserContent, useComposerContentStore } from "~/composerDraftStore";
import { cn } from "~/lib/utils";

/**
 * UP13 — a session row holding an UNSENT draft wears a pen (upstream #9658).
 * The row subscribes to one boolean from the draft store, so typing in the
 * open session re-renders nothing but that session's own selector; the
 * addressed session never shows the mark (its composer is in front of you).
 * Hover reveals a discard X that clears the draft without opening the row.
 */
export function useSessionDraftMark(threadRef: ScopedThreadRef | null): boolean {
  const key = threadRef === null ? null : scopedThreadKey(threadRef);
  return useComposerContentStore(
    (state) => key !== null && composerDraftHasUserContent(state.draftsByThreadKey[key]),
  );
}

export function discardSessionDraft(threadRef: ScopedThreadRef): void {
  useComposerContentStore.getState().clearComposerContent(threadRef);
}

export function RoomRailDraftMark({
  label,
  sessionId,
  threadRef,
  current,
  group = "session",
}: {
  readonly label: string;
  readonly sessionId: string;
  readonly threadRef: ScopedThreadRef | null;
  readonly current: boolean;
  /** The row's hover group: chips use `session`, sub-session rows `row`. */
  readonly group?: "session" | "row";
}): ReactElement | null {
  const draft = useSessionDraftMark(threadRef);
  if (!draft || current || threadRef === null) return null;
  return (
    <span
      className="flex shrink-0 items-center gap-0.5"
      data-room-rail-draft={sessionId}
      title="Unsent draft"
    >
      <PencilLine aria-hidden="true" className="size-3 text-amber-600 dark:text-amber-400" />
      <span className="sr-only">Unsent draft</span>
      <button
        type="button"
        aria-label={`Discard draft in ${label}`}
        title="Discard draft"
        data-room-rail-draft-discard={sessionId}
        className={cn(
          "pointer-events-none flex size-3.5 items-center justify-center rounded-sm text-muted-foreground opacity-0 hover:bg-accent hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100",
          group === "session"
            ? "group-hover/session:pointer-events-auto group-hover/session:opacity-70 group-focus-within/session:pointer-events-auto group-focus-within/session:opacity-70"
            : "group-hover/row:pointer-events-auto group-hover/row:opacity-70 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-70",
        )}
        onClick={(event) => {
          event.stopPropagation();
          discardSessionDraft(threadRef);
        }}
        onDoubleClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onContextMenu={(event) => event.stopPropagation()}
      >
        <X aria-hidden="true" className="size-2.5" />
      </button>
    </span>
  );
}
